//#region types
import { FormEnums } from "models/enums";
import { GenericFormSection } from "models/propTypes/genericFormProps";
import { ArticleFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { FilterQueryStateHandler } from "models/propTypes/filterPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { SelectionItem } from "models/utilTypes/selectionTypes";
//#region components
import { useInvoiceService } from "serviceHooks";
//#endregion
import { DateHelper, FilterHelper, QueryStateHelper } from "utils/helpers";

function useArticleReportFilterDefinition() {
  const invoiceService = useInvoiceService();
  const apiToReportFilterFormatter = async (filter: WintSuperkollAPIModel.ArticleFilter) => {
    const reportFilter: ArticleFormTypes.Filter = {};

    if (filter.unitId !== undefined) {
      reportFilter.unitId = filter.unitId;
    }

    if (filter.updatedAfter !== undefined) {
      reportFilter.updatedAfter = DateHelper.fromAPIDate(filter.updatedAfter);
    }

    if (filter.inactive !== undefined) {
      reportFilter.active = !filter.inactive;
    }

    if (filter.name !== undefined) {
      reportFilter.name = filter.name;
    }

    if (filter.euVatProductType !== undefined) {
      reportFilter.euVatProductType = invoiceService
        .getEuVatCategoryTypes()
        .filter((t) => filter.euVatProductType?.includes(t.id as number)) as SelectionItem[];
    }

    if (filter.unitPriceFrom !== undefined || filter.unitPriceTo !== undefined) {
      reportFilter.unitPrice = { lowest: filter.unitPriceFrom, highest: filter.unitPriceTo };
    }

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.Article.Filter.TotalAmountForLastYearlyPeriod,
      filter.totalAmountForLastYearlyPeriodFrom,
      filter.totalAmountForLastYearlyPeriodTo
    );

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.Article.Filter.TotalQuantityForLastYearlyPeriod,
      filter.totalQuantityForLastYearlyPeriodFrom,
      filter.totalQuantityForLastYearlyPeriodTo
    );

    return reportFilter;
  };

  const filterFormFields: GenericFormSection<ArticleFormTypes.Filter>[] = [
    {
      label: TranslationKeys.Common.General,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        name: {
          label: TranslationKeys.Common.Name,
          type: FormEnums.FormFieldType.TEXT,
        },
        euVatProductType: {
          label: TranslationKeys.Article.EuVatProductType,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: invoiceService.getEuVatCategoryTypes(),
          },
        },
        active: {
          label: TranslationKeys.Common.Active,
          type: FormEnums.FormFieldType.BOOLEANSELECTION,
        },
      },
    },
    {
      label: TranslationKeys.Common.Amount,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        unitPrice: {
          label: TranslationKeys.Common.StandardPrice,
          type: FormEnums.FormFieldType.CURRENCYRANGE,
        },
        totalQuantityForLastYearlyPeriod: {
          label: TranslationKeys.Article.TotalQuantityForLastYearlyPeriod,
          type: FormEnums.FormFieldType.NUMBERRANGE,
        },
        totalAmountForLastYearlyPeriod: {
          label: TranslationKeys.Article.TotalAmountForLastYearlyPeriod,
          type: FormEnums.FormFieldType.CURRENCYRANGE,
        },
      },
    },
  ];

  const filterQueryStateHandler: FilterQueryStateHandler<ArticleFormTypes.Filter> = {
    unitId: QueryStateHelper.numberQueryEncoder(FormFieldConstants.Article.Filter.UnitId),
    updatedAfter: QueryStateHelper.dateQueryEncoder(FormFieldConstants.Article.Filter.UpdatedAfter),
    active: QueryStateHelper.booleanQueryEncoder(FormFieldConstants.Article.Filter.Active),
    name: QueryStateHelper.stringQueryEncoder(FormFieldConstants.Article.Filter.Name),
    euVatProductType: QueryStateHelper.selectionItemsQueryEncoder(
      FormFieldConstants.Article.Filter.EuVatProductType,
      invoiceService.getEuVatCategoryTypes().map((v) => ({ id: v.id ?? 0, value: v.value }))
    ),
    unitPrice: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.Article.Filter.UnitPrice),
    totalAmountForLastYearlyPeriod: QueryStateHelper.numberRangeQueryEncoder(
      FormFieldConstants.Article.Filter.TotalAmountForLastYearlyPeriod
    ),
    totalQuantityForLastYearlyPeriod: QueryStateHelper.numberRangeQueryEncoder(
      FormFieldConstants.Article.Filter.TotalQuantityForLastYearlyPeriod
    ),
  };

  return { filterFormFields, filterQueryStateHandler, apiToReportFilterFormatter };
}

export default useArticleReportFilterDefinition;
